<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Play History</span>
      <h1>See Your Full Steam Play History &amp; Session Timeline</h1>
      <p>
        Steam only shows you "recently played" for the last two weeks. steamplayti.me keeps a running daily
        timeline of every session, so you can see exactly when you played each game — not just how many hours
        it adds up to.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">View your play history</router-link>
        <a class="seo-secondary-link" href="#history-timeline">How the timeline works →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">🗓️</span>
          <div>Every day you played gets its own entry — no more guessing when a session happened.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">⏱️</span>
          <div>See per-day, per-game minutes going back further than Steam's own two-week window.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">📈</span>
          <div>Spot streaks, breaks, and comebacks at a glance instead of scrolling Steam's profile activity.</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="history-timeline">
      <h2>A Daily Timeline, Not Just a Total</h2>
      <p>
        Steam's own profile only tells you total hours and a rough "recently played" list. steamplayti.me fills
        the gap by recording a daily snapshot per game, so your play history reads like a calendar instead of a
        single lifetime number.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Session-Level Detail</h3>
          <p>Each day's playtime increment is tracked separately, so a marathon weekend and a quiet week both show clearly.</p>
        </div>
        <div class="seo-card">
          <h3>Beyond Two Weeks</h3>
          <p>Steam's "recently played" resets after 14 days. Your steamplayti.me history keeps accumulating for as long as we've tracked your account.</p>
        </div>
        <div class="seo-card">
          <h3>Per-Game Breakdown</h3>
          <p>Filter the timeline down to a single title to see exactly which days you picked it back up.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Why a Play History Matters</h2>
      <p>
        Whether you're settling a "when did I even play that" argument, tracking a habit, or just curious about
        your own patterns, a real timeline beats a single running total.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Granularity</div>
          <div class="value">Daily</div>
        </div>
        <div class="seo-stat">
          <div class="label">Lookback</div>
          <div class="value">Since tracking began</div>
        </div>
        <div class="seo-stat">
          <div class="label">Cost</div>
          <div class="value">Free</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Steam Play History FAQ</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>Does Steam keep a play history?</h3>
          <p>Steam shows a rolling "recently played" window (about two weeks) plus a lifetime total per game, but it doesn't expose a day-by-day timeline. That's the gap steamplayti.me fills.</p>
        </div>
        <div class="seo-faq-item">
          <h3>How far back does my history go?</h3>
          <p>As far back as steamplayti.me has been tracking your Steam ID — every day gets recorded, so the timeline only grows the longer you use it.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I see history for a single game?</h3>
          <p>Yes. Search your Steam ID and drill into any game from your dashboard to see just that title's daily sessions.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Does it work for private profiles?</h3>
          <p>We can only track what Steam's API exposes. If your game details or playtime are set to private, toggle them to public in your Steam privacy settings first.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-play-history" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const META_DESCRIPTION = 'See your full Steam play history as a daily timeline, not just a lifetime total. steamplayti.me tracks every session beyond Steam\'s two-week recently-played window.';

const META = {
  title: 'Steam Play History & Session Timeline | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'Steam play history, Steam session history, Steam play time history, Steam recently played, Steam session timeline',
  canonical: 'https://steamplayti.me/page/steam-play-history/',
  ogTitle: 'Steam Play History & Session Timeline',
  ogDescription: 'Track a real daily timeline of your Steam sessions — further back than Steam\'s own recently-played list.',
  ogUrl: 'https://steamplayti.me/page/steam-play-history/',
  twitterTitle: 'Steam Play History & Session Timeline',
  twitterDescription: 'See exactly when you played each Steam game with a daily history timeline from steamplayti.me.',
  structuredData: pageStructuredData({
    name: 'Steam Play History',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-play-history/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Play History', path: 'https://steamplayti.me/page/steam-play-history/' },
    ],
  }),
};

export default {
  name: 'SteamPlayHistoryPage',
  components: { SeoRelatedTools },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
